import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { ThemeProvider } from "./lib/theme";
import { trackPageview, installPhoneClickTracker } from "./lib/analytics";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import OrganizationSchema from "./components/seo/OrganizationSchema";
import { CurrencyProvider } from "./lib/currency";

// ─── Route code-splitting ─────────────────────────────────────────
// Only the home page is imported eagerly — every other route ships as
// its own chunk that the browser only downloads on first visit. Cuts the
// initial JS payload from ~1.2 MB to ~180 KB and lets navigation land
// under one blink after the browser has parsed the home-page shell.
// (React automatically deduplicates already-loaded chunks, so a repeat
// visit to the same page is instant.)
const HomePage = lazy(() => import("./pages/HomePage"));
const ReturnPolicyPage    = lazy(() => import("./pages/ReturnPolicyPage"));
const ShippingPolicyPage  = lazy(() => import("./pages/ShippingPolicyPage"));
const PrivacyPolicyPage   = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsPage           = lazy(() => import("./pages/TermsPage"));
const ProductsPage        = lazy(() => import("./pages/ProductsPage"));
const ProductDetailPage   = lazy(() => import("./pages/ProductDetailPage"));
const AboutPage           = lazy(() => import("./pages/AboutPage"));
const ServicesPage        = lazy(() => import("./pages/ServicesPage"));
const PartnersPage        = lazy(() => import("./pages/PartnersPage"));
const ContactPage         = lazy(() => import("./pages/ContactPage"));
const AdminPage           = lazy(() => import("./pages/AdminPage"));
const AzureCalculatorPage = lazy(() => import("./pages/AzureCalculatorPage"));
const MicrosoftAzurePage  = lazy(() => import("./pages/MicrosoftAzurePage"));
const EstimatePage        = lazy(() => import("./pages/EstimatePage"));
const AutodeskPage        = lazy(() => import("./pages/AutodeskPage"));
const CorelPage           = lazy(() => import("./pages/CorelPage"));
const SketchupPage        = lazy(() => import("./pages/SketchupPage"));
const AdobePage           = lazy(() => import("./pages/AdobePage"));
const ZohoPage            = lazy(() => import("./pages/ZohoPage"));
const M365BusinessPage    = lazy(() => import("./pages/M365BusinessPage"));
const M365EnterprisePage  = lazy(() => import("./pages/M365EnterprisePage"));
const M365FrontlinePage   = lazy(() => import("./pages/M365FrontlinePage"));
const MicrosoftHotSellersPage = lazy(() => import("./pages/MicrosoftHotSellersPage"));
const MicrosoftPage       = lazy(() => import("./pages/MicrosoftPage"));
const Microsoft365Page    = lazy(() => import("./pages/Microsoft365Page"));
const MicrosoftDynamicsPage = lazy(() => import("./pages/MicrosoftDynamicsPage"));
const MicrosoftSubscriptionsPage = lazy(() => import("./pages/MicrosoftSubscriptionsPage"));
const TallyPage          = lazy(() => import("./pages/TallyPage"));
const QuickHealPage      = lazy(() => import("./pages/QuickHealPage"));
const SeqritePage        = lazy(() => import("./pages/SeqritePage"));
const ChaosPage          = lazy(() => import("./pages/ChaosPage"));
const ChaosFamilyPage    = lazy(() => import("./pages/ChaosFamilyPage"));
const SearchPage          = lazy(() => import("./pages/SearchPage"));
const ServiceDetailPage   = lazy(() => import("./pages/ServiceDetailPage"));
const ManagedServicesPage = lazy(() => import("./pages/ManagedServicesPage"));
const SoftwareWebDevPage  = lazy(() => import("./pages/SoftwareWebDevPage"));
const HardwarePage        = lazy(() => import("./pages/HardwarePage"));
const HpeConfiguratorPage = lazy(() => import("./pages/HpeConfiguratorPage"));
const CategoryLandingPage = lazy(() => import("./pages/CategoryLandingPage"));
const HtmlSitemapPage     = lazy(() => import("./pages/HtmlSitemapPage"));
const OpenValueRedirect   = lazy(() => import("./pages/OpenValueRedirect"));
const KeywordGapPage      = lazy(() => import("./pages/KeywordGapPage"));
const BuyPage             = lazy(() => import("./pages/BuyPage"));
const BlogListPage        = lazy(() => import("./pages/BlogListPage"));
const BlogPostPage        = lazy(() => import("./pages/BlogPostPage"));
const AdminBlogPage       = lazy(() => import("./pages/AdminBlogPage"));
const AdminIntegrationsPage = lazy(() => import("./pages/AdminIntegrationsPage"));
const AdminChaosFamiliesPage = lazy(() => import("./pages/AdminChaosFamiliesPage"));
const AdminBlogEditPage   = lazy(() => import("./pages/AdminBlogEditPage"));
const DevelopersMcpPage   = lazy(() => import("./pages/DevelopersMcpPage"));
const OAuthConsentPage    = lazy(() => import("./pages/OAuthConsentPage"));
const PaymentReturnPage   = lazy(() => import("./pages/PaymentReturnPage"));
const CheckoutResumePage  = lazy(() => import("./pages/CheckoutResumePage"));
const NotFoundPage        = lazy(() => import("./pages/NotFoundPage"));

/** Skeleton shown while a lazy chunk downloads (usually 40-200 ms). */
function ChunkSkeleton() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center" role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-2">
        <div className="h-6 w-24 rounded-sm bg-brand-blue/10 animate-pulse" />
        <div className="h-3 w-40 rounded-sm bg-muted animate-pulse" />
        <div className="h-3 w-32 rounded-sm bg-muted animate-pulse" />
        <span className="sr-only">Loading…</span>
      </div>
    </div>
  );
}

function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <OrganizationSchema />
      <Header />
      <main className="flex-1 min-h-screen"><Suspense fallback={<ChunkSkeleton />}>{children}</Suspense></main>
      <Footer />
    </div>
  );
}

/** Fires a GA4 page_view on every React Router navigation (SPA-safe). */
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    // Defer to next tick so document.title (updated by Helmet) has settled.
    const t = setTimeout(() => trackPageview(location.pathname + location.search), 0);
    return () => clearTimeout(t);
  }, [location.pathname, location.search]);
  return null;
}

/**
 * Legacy /blog/:slug → /resources/blog/:slug client redirect. Keeps
 * external bookmarks, existing GSC impressions, and old email links
 * pointing at the fresh canonical URL without adding a 404 hop.
 */
function LegacyBlogRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/resources/blog/${slug}`} replace />;
}

/**
 * /call — opens the phone dialer on mobile (or the OS handler on desktop) and
 * shows a tiny fallback so the visitor sees the number even if the browser
 * blocks the tel: navigation. Used for QR codes and short marketing links.
 */
function CallRedirect() {
  useEffect(() => {
    const t = setTimeout(() => { window.location.href = "tel:+918527694100"; }, 50);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">India Enterprises · Sales</div>
      <h1 className="font-display font-black text-3xl sm:text-4xl text-brand-navy dark:text-white">Connecting your call…</h1>
      <p className="text-sm text-muted-foreground max-w-md">If your phone did not open automatically, tap the button below.</p>
      <a href="tel:+918527694100" data-testid="call-redirect-cta"
         className="inline-flex items-center gap-2 rounded-sm bg-brand-electric text-brand-navy px-8 py-3.5 text-base font-black hover:bg-brand-electric/90 transition-colors shadow-lg">
        Call +91 85276 94100
      </a>
    </div>
  );
}

function App() {
  // Remove the static <title> and <meta name="description"> baked into
  // public/index.html once react-helmet-async has mounted its own SEO
  // tags. Prevents the DOM from containing duplicate head tags on client-
  // side navigation (Google Search Console flags duplicates as a warning).
  // The static tags are still shipped in the raw HTML so JS-off SEO
  // auditors keep receiving them on first request.
  useEffect(() => {
    const drop = document.querySelectorAll('[data-static-seo="true"]');
    drop.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
  }, []);

  // Google Ads "Phone call click" conversion tracker (Action ID 7732243567).
  // Installs a single document-level click delegator so every `<a href="tel:">`
  // link across the site fires the conversion without per-component wiring.
  useEffect(() => {
    return installPhoneClickTracker();
  }, []);

  return (
    <div className="App">
      <HelmetProvider>
        <ThemeProvider>
          <CurrencyProvider>
            <BrowserRouter>
              <ScrollToTop />
              <RouteTracker />
              <Toaster position="top-right" richColors theme="system" />
              <Routes>
                <Route path="/" element={<Layout><HomePage /></Layout>} />
                <Route path="/call" element={<Layout><CallRedirect /></Layout>} />
                <Route path="/return-policy" element={<Layout><ReturnPolicyPage /></Layout>} />
                <Route path="/shipping-policy" element={<Layout><ShippingPolicyPage /></Layout>} />
                <Route path="/privacy" element={<Layout><PrivacyPolicyPage /></Layout>} />
                {/* Feb 2026 Fix Pack — legacy alias 301→canonical /privacy */}
                <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
                <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
                <Route path="/products" element={<Layout><ProductsPage /></Layout>} />
                <Route path="/microsoft" element={<Layout><MicrosoftPage /></Layout>} />
                <Route path="/microsoft-365" element={<Layout><Microsoft365Page /></Layout>} />
                <Route path="/microsoft-dynamics" element={<Layout><MicrosoftDynamicsPage /></Layout>} />
                <Route path="/microsoft-subscriptions" element={<Layout><MicrosoftSubscriptionsPage /></Layout>} />
                <Route path="/tally" element={<Layout><TallyPage /></Layout>} />
                <Route path="/quickheal" element={<Layout><QuickHealPage /></Layout>} />
                <Route path="/seqrite" element={<Layout><SeqritePage /></Layout>} />
                <Route path="/chaos" element={<Layout><ChaosPage /></Layout>} />
                <Route path="/chaos/:slug" element={<Layout><ChaosFamilyPage /></Layout>} />
                <Route path="/search" element={<Layout><SearchPage /></Layout>} />
                <Route path="/microsoft-365-business" element={<Layout><M365BusinessPage /></Layout>} />
                {/* 30 Aug 2026 — three separate Microsoft 365 landing
                    pages (Business / Enterprise / Frontline) — one per
                    tier family, each with its own SEO title, H1 and
                    schema. Business page is the flagship. */}
                <Route path="/microsoft-365-enterprise" element={<Layout><M365EnterprisePage /></Layout>} />
                <Route path="/microsoft-365-frontline"  element={<Layout><M365FrontlinePage /></Layout>} />
                <Route path="/microsoft-hot-sellers" element={<Layout><MicrosoftHotSellersPage /></Layout>} />
                <Route path="/zoho" element={<Layout><ZohoPage /></Layout>} />
                <Route path="/products/:id" element={<Layout><ProductDetailPage /></Layout>} />
                <Route path="/about" element={<Layout><AboutPage /></Layout>} />
                <Route path="/services" element={<Layout><ServicesPage /></Layout>} />
                <Route path="/services/:slug" element={<Layout><ServiceDetailPage /></Layout>} />
                <Route path="/managed-services" element={<Layout><ManagedServicesPage /></Layout>} />
                <Route path="/software-web-development" element={<Layout><SoftwareWebDevPage /></Layout>} />
                <Route path="/partners" element={<Layout><PartnersPage /></Layout>} />
                <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
                <Route path="/azure-calculator" element={<Layout><AzureCalculatorPage /></Layout>} />
                <Route path="/microsoft-azure"  element={<Layout><MicrosoftAzurePage /></Layout>} />
                {/* Legacy dedicated Open Value page removed — all OV products now
                    live under the Microsoft Open brand tile on /products. This
                    redirect keeps external / bookmarked / sitemap URLs alive. */}
                <Route path="/open-value" element={<OpenValueRedirect />} />
                <Route path="/estimate" element={<Layout><EstimatePage /></Layout>} />
                <Route path="/autodesk" element={<Layout><AutodeskPage /></Layout>} />
                <Route path="/corel" element={<Layout><CorelPage /></Layout>} />
                <Route path="/sketchup" element={<Layout><SketchupPage /></Layout>} />
                <Route path="/adobe" element={<Layout><AdobePage /></Layout>} />
                <Route path="/hardware" element={<Layout><HardwarePage /></Layout>} />
                <Route path="/hardware/configure/:id" element={<Layout><HpeConfiguratorPage /></Layout>} />
                <Route path="/category/:slug" element={<Layout><CategoryLandingPage /></Layout>} />
                <Route path="/price-list/:slug" element={<Layout><KeywordGapPage family="price-list" /></Layout>} />
                <Route path="/guides/:slug" element={<Layout><KeywordGapPage family="guides" /></Layout>} />
                <Route path="/industries/:slug" element={<Layout><KeywordGapPage family="industries" /></Layout>} />
                <Route path="/buy/:slug" element={<Layout><BuyPage /></Layout>} />
                {/* Blog — canonical URLs are /resources/blog[/*] per user brief.
                    Older /blog[/*] paths continue to work via client redirect
                    so external bookmarks and existing Google impressions
                    resolve without a 404 hop. */}
                <Route path="/resources/blog" element={<Layout><BlogListPage /></Layout>} />
                <Route path="/resources/blog/:slug" element={<Layout><BlogPostPage /></Layout>} />
                <Route path="/blog" element={<Navigate to="/resources/blog" replace />} />
                <Route path="/blog/:slug" element={<LegacyBlogRedirect />} />
                {/* Admin blog CRUD — mounted lazily like AdminPage since
                    /admin/blog is a large chunk (React-Quill editor + form). */}
                <Route path="/admin/blog" element={<Suspense fallback={<ChunkSkeleton />}><AdminBlogPage /></Suspense>} />
                <Route path="/admin/integrations" element={<Suspense fallback={<ChunkSkeleton />}><AdminIntegrationsPage /></Suspense>} />
                <Route path="/admin/blog/new" element={<Suspense fallback={<ChunkSkeleton />}><AdminBlogEditPage /></Suspense>} />
                <Route path="/admin/blog/edit/:id" element={<Suspense fallback={<ChunkSkeleton />}><AdminBlogEditPage /></Suspense>} />
                <Route path="/admin/chaos-families" element={<Suspense fallback={<ChunkSkeleton />}><AdminChaosFamiliesPage /></Suspense>} />
                <Route path="/sitemap" element={<Layout><HtmlSitemapPage /></Layout>} />
                <Route path="/admin" element={<Suspense fallback={<ChunkSkeleton />}><AdminPage /></Suspense>} />

                {/* Public developer docs for the Admin MCP endpoint +
                    OAuth 2.1 consent screen for ChatGPT custom apps. */}
                <Route path="/developers/mcp" element={<Layout><DevelopersMcpPage /></Layout>} />
                <Route path="/oauth/consent" element={<Layout><OAuthConsentPage /></Layout>} />

                {/* Cashfree hosted-checkout return URL — server-verifies
                    the payment before rendering success/failure so the
                    outcome is never spoofable by the client. */}
                <Route path="/payment-return" element={<Layout><PaymentReturnPage /></Layout>} />

                {/* Abandoned-order resume link — target of the 1h/24h/72h
                    reminder emails. Server verifies the token, then we
                    bounce to the source product page with a special
                    query flag so the Buy Now dialog opens pre-filled. */}
                <Route path="/checkout/resume" element={<Layout><CheckoutResumePage /></Layout>} />

                {/* Legacy /subcategory/* URLs (previously in sitemap but never
                    routed) → 301-style client redirect to the parent /products
                    grid so Google can consolidate signals. Emits noindex on
                    the transient render so the URL is removed from the index. */}
                <Route path="/subcategory/:slug" element={<Navigate to="/products" replace />} />
                <Route path="/subcategory" element={<Navigate to="/products" replace />} />

                {/* Catch-all — serves a proper noindex 404 for anything else.
                    Removes the "Soft 404" bucket in GSC by giving unknown
                    URLs a clear "removed" signal instead of blank layout. */}
                <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
              </Routes>
            </BrowserRouter>
          </CurrencyProvider>
        </ThemeProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
